import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let cache = null;
let inflight = null;

export function loadSeoHead() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = axios
      .get(`${API}/seo/head`)
      .then((r) => {
        cache = r.data;
        return cache;
      })
      .catch(() => null);
  }
  return inflight;
}

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectRaw(html, id, target = document.head) {
  if (!html || document.querySelector(`[data-si-inject="${id}"]`)) return;
  const frag = document.createRange().createContextualFragment(html);
  const wrapper = document.createElement("span");
  wrapper.setAttribute("data-si-inject", id);
  wrapper.style.display = "none";
  target.appendChild(wrapper);
  target.appendChild(frag);
}

function injectScript(id, src, attrs = {}, inline) {
  if (document.querySelector(`script[data-si-script="${id}"]`)) return;
  const s = document.createElement("script");
  s.setAttribute("data-si-script", id);
  s.async = true;
  if (src) s.src = src;
  if (inline) s.text = inline;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function setupTracking(data) {
  const a = data.analytics || {};
  const ads = data.ads || {};
  if (a.respect_dnt && navigator.doNotTrack === "1") return;
  if (a.enabled !== false) {
    if (a.ga4_id) {
      injectScript("ga4-lib", `https://www.googletagmanager.com/gtag/js?id=${a.ga4_id}`);
      injectScript(
        "ga4-init",
        null,
        {},
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${a.ga4_id}');`
      );
    }
    if (a.gtm_id) {
      injectScript(
        "gtm",
        null,
        {},
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${a.gtm_id}');`
      );
    }
    if (a.fb_pixel_id) {
      injectScript(
        "fbq",
        null,
        {},
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${a.fb_pixel_id}');fbq('track','PageView');`
      );
    }
    if (a.clarity_id) {
      injectScript(
        "clarity",
        null,
        {},
        `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${a.clarity_id}");`
      );
    }
    upsertMeta("name", "google-site-verification", a.google_site_verification);
    upsertMeta("name", "msvalidate.01", a.bing_verification);
    upsertMeta("name", "yandex-verification", a.yandex_verification);
    upsertMeta("name", "p:domain_verify", a.pinterest_verification);
    injectRaw(a.custom_head_code, "custom-head");
    injectRaw(a.custom_body_code, "custom-body", document.body);
  }

  if (ads.enabled) {
    if (ads.publisher_id && ads.auto_ads) {
      injectScript("adsense", `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ads.publisher_id}`, {
        crossorigin: "anonymous",
      });
    }
    injectRaw(ads.head_code, "ads-head");
  }
}

export default function SeoHead() {
  const { pathname } = useLocation();
  const initialised = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadSeoHead().then((data) => {
      if (!data || cancelled) return;
      const isAdmin = pathname.startsWith("/admin");
      const seo = data.seo || {};
      const base = data.base_url || window.location.origin;
      const page = (data.pages || []).find((p) => p.path === pathname) || {};

      const title = page.title || seo.meta_title || seo.site_title;
      const desc = page.description || seo.meta_description;
      if (title) document.title = title;
      upsertMeta("name", "description", desc);
      upsertMeta("name", "keywords", page.keywords || seo.keywords);
      upsertMeta(
        "name",
        "robots",
        page.noindex || isAdmin
          ? "noindex, nofollow"
          : "index, follow, max-image-preview:large, max-snippet:-1"
      );
      upsertLink("canonical", page.canonical || `${base}${pathname}`);
      upsertMeta("property", "og:title", title);
      upsertMeta("property", "og:description", desc);
      upsertMeta("property", "og:url", `${base}${pathname}`);
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:site_name", seo.organization_name || "Simple Invoice");
      upsertMeta("property", "og:image", page.og_image || seo.default_og_image || seo.og_image);
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:site", seo.twitter_handle);
      upsertMeta("name", "twitter:title", title);
      upsertMeta("name", "twitter:description", desc);
      upsertMeta("name", "twitter:image", page.og_image || seo.default_og_image || seo.og_image);

      if (Array.isArray(data.schema) && data.schema.length) {
        let el = document.getElementById("si-jsonld");
        if (!el) {
          el = document.createElement("script");
          el.type = "application/ld+json";
          el.id = "si-jsonld";
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data.schema);
      }

      if (!initialised.current && !isAdmin) {
        initialised.current = true;
        setupTracking(data);
      }

      if (!isAdmin) {
        axios
          .post(`${API}/analytics/track`, {
            path: pathname,
            event: "pageview",
            referrer: document.referrer || "",
          })
          .catch(() => {});
      }
    });
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}
